/**
 * Pinned "Ingested Documents" system folder in the Documents library.
 * Real folder rows created by unstructured ingest (path `ingested`) reuse
 * the same path; the virtual row only fills in when the API has none yet.
 */

import {
  INGESTED_LIBRARY_PREFIX,
  ingestMetaFromDoc,
  isIngestedLibraryPath,
  type Doc,
  type DocFolder
} from '@axilon-apps/domains-ontology-ui-state'

export const INGESTED_DOCUMENTS_FOLDER_ID = '__axilon_ingested_documents_folder__'
export const INGESTED_DOCUMENTS_FOLDER_PATH = INGESTED_LIBRARY_PREFIX
export const INGESTED_DOCUMENTS_FOLDER_NAME = 'Ingested Documents'
export const isIngestedDocumentsPath = isIngestedLibraryPath

export const INGESTED_DOCUMENTS_FOLDER: DocFolder = {
  id: INGESTED_DOCUMENTS_FOLDER_ID,
  name: INGESTED_DOCUMENTS_FOLDER_NAME,
  path: INGESTED_DOCUMENTS_FOLDER_PATH,
  created_at: '',
  updated_at: ''
}

/** Header / paste destinations under ingested remap to the library root. */
export function writableDocsCreateParent(path: string): string {
  return isIngestedDocumentsPath(path) ? '' : path
}

/** Prepend the virtual folder (or move a real `ingested` row to the front). */
export function withIngestedDocumentsVirtual(
  folders: readonly DocFolder[],
  documents: readonly Doc[] = []
): DocFolder[] {
  const runNames = new Map<string, string>()
  for (const document of documents) {
    const ingest = ingestMetaFromDoc(document)
    const runName = ingest?.runName?.trim()
    if (ingest?.jobId !== undefined && runName !== undefined && runName.length > 0) {
      runNames.set(`${INGESTED_DOCUMENTS_FOLDER_PATH}/${ingest.jobId}`, runName)
    }
  }

  const namedFolders = folders.map((folder) => {
    const runName = runNames.get(folder.path)
    return runName === undefined ? folder : { ...folder, name: runName }
  })
  const existing = namedFolders.find((folder) => folder.path === INGESTED_DOCUMENTS_FOLDER_PATH)
  if (existing === undefined) {
    return [INGESTED_DOCUMENTS_FOLDER, ...namedFolders]
  }
  return [
    existing,
    ...namedFolders.filter((folder) => folder.path !== INGESTED_DOCUMENTS_FOLDER_PATH)
  ]
}
